/**
 * Google Ads conversion firing for the caregiver app.
 *
 * The gtag.js library and Consent Mode are bootstrapped in index.html +
 * rmv-tag.js (loaded cross-origin from the marketing origin). This module is
 * the typed, app-side surface for firing the two manual-event conversions —
 * signup (primary) and purchase (secondary) — and for reading the gclid that
 * rmv-tag.js captured into the shared `.remindmevoice.com` cookie.
 *
 * Consent is handled upstream by Consent Mode: if the visitor hasn't accepted
 * advertising cookies, gtag withholds the conversion automatically. So callers
 * fire unconditionally — we don't gate here.
 *
 * Each conversion must fire at most once. We guard with a localStorage set
 * keyed by a stable id (uid for signup, Stripe session id for purchase) so a
 * re-render, back-navigation, or page refresh can't double-count.
 */

import { isProdHost } from './prodHost';

// Conversion "send_to" targets (Ads account AW-18265049579). These aren't
// secret; kept as named constants so they're not scattered inline. Override
// via Vite env (VITE_ADS_*) if they ever need to differ per environment.
const SIGNUP_SEND_TO =
  import.meta.env.VITE_ADS_SIGNUP_SEND_TO ?? 'AW-18265049579/dv2qCOPNmMQcEOuTuoVE';
const PURCHASE_SEND_TO =
  import.meta.env.VITE_ADS_PURCHASE_SEND_TO ?? 'AW-18265049579/kRWtCObNmMQcEOuTuoVE';

const FIRED_KEY = 'rmv-ads-fired';

/**
 * Flat estimated value (GBP) reported on the purchase conversion. We
 * deliberately do NOT report the Stripe charge: the £79 hardware is roughly
 * break-even — the real value is the ongoing £14.99/mo subscription — and the
 * charge scales with button quantity while a customer's value doesn't (one
 * subscription covers all their buttons). A flat estimate is both simpler and a
 * better signal for value-based bidding.
 *
 * Placeholder until we have churn/LTV data. Changing it only affects reporting
 * today — the campaign is on Maximise Clicks, which ignores conversion value.
 */
export const ESTIMATED_PURCHASE_VALUE_GBP = 150;

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

function gtag(...args: unknown[]): void {
  // rmv-tag.js defines window.gtag; if it failed to load (offline/dev) we
  // no-op rather than throw — conversion tracking is best-effort.
  if (typeof window.gtag === 'function') window.gtag(...args);
}

/** Returns true the first time it's called for `id`, false thereafter
 * (persisted across reloads). Used so each conversion fires exactly once. */
function claimOnce(id: string): boolean {
  try {
    const raw = localStorage.getItem(FIRED_KEY);
    const fired: string[] = raw ? JSON.parse(raw) : [];
    if (fired.includes(id)) return false;
    fired.push(id);
    localStorage.setItem(FIRED_KEY, JSON.stringify(fired));
    return true;
  } catch {
    // localStorage unavailable — fire once per page load at worst.
    return true;
  }
}

/** Read the gclid (or gbraid/wbraid) captured by rmv-tag.js from the
 * cross-subdomain cookie. Null if the visitor didn't arrive from an ad (or
 * declined advertising cookies, in which case rmv-tag.js never wrote it). */
export function readGclid(): string | null {
  const match = document.cookie.match(/(?:^|; )rmv_gclid=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** Fire the primary signup conversion with Enhanced Conversions.
 *
 * @param uid    Firebase uid — single-fire key.
 * @param email  The address the user entered at signup. Passed UNHASHED; the
 *               Google tag hashes it client-side (SHA-256). Do not pre-hash. */
export function fireSignupConversion(uid: string, email: string): void {
  if (!isProdHost()) return;
  if (!claimOnce(`signup:${uid}`)) return;
  const params: Record<string, unknown> = { send_to: SIGNUP_SEND_TO };
  // Only attach Enhanced Conversions data when we actually have an email — an
  // empty user_data submission just shows up as a failed EC match in the
  // diagnostics. (Google accounts always have one; this guards the `?? ''`.)
  if (email) params.user_data = { email };
  gtag('event', 'conversion', params);
}

/** Fire the secondary purchase conversion.
 *
 * @param transactionId  Stripe Checkout session id — single-fire key and the
 *                       de-dupe id Google uses across repeated fires.
 * @param valueGbp       Flat estimated customer value (ESTIMATED_PURCHASE_VALUE_GBP);
 *                       NOT the Stripe charge — see that constant's doc. */
export function firePurchaseConversion(transactionId: string, valueGbp: number): void {
  if (!isProdHost()) return;
  if (!claimOnce(`purchase:${transactionId}`)) return;
  gtag('event', 'conversion', {
    send_to: PURCHASE_SEND_TO,
    value: valueGbp,
    currency: 'GBP',
    transaction_id: transactionId,
  });
}
